/* AIWatchdogOpportunities — surfaces AI watchdog service opportunities.
 *
 * Renders in 2 modes:
 *   • inline (contactId prop) — shows opportunities for ONE client
 *   • global (no contactId) — shows opportunities across all clients (dashboard widget)
 *
 * Each card displays the AI alert (refix/anniversary/investment/pension) with
 * suggested action; button "Vytvořit task + deal" activates the opportunity
 * which creates a task in Diář and a deal in pipeline (idempotent — re-clicking
 * re-uses existing records).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { KROK, MAX_KROKU, UI, EASE_OUT, useBezPohybu } from "@/lib/pohyb";
import {
  BellRing, RefreshCw, RotateCcw, ArrowRight, AlertTriangle, Clock, Briefcase, Coins, TrendingUp, Shield,
  CalendarPlus, ChevronRight, X as XIcon, Check,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { openClientFolder } from "@/lib/openClientFolder";
import { navigateOverlaySafe } from "@/lib/paletteNav";
import { localeUI } from "@/lib/prekladyUI";

// Robustnost: opportunities může přijít jako ne-pole nebo s null-položkami.
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

const KIND_META = {
  /* `text` = drobný text štítku (tmavší odstín, ≥ 4,5:1), `color` = proužek, rámeček, ikona. */
  refix:       { color: "var(--warn)",     text: "var(--warn-text)",     icon: Briefcase,   label: "Refixace" },
  anniversary: { color: "var(--info)",     text: "var(--info-text)",     icon: Shield,      label: "Výročí" },
  investment:  { color: "var(--accent)",            text: "var(--accent)",        icon: TrendingUp,  label: "Audit" },
  pension:     { color: "var(--positive)", text: "var(--positive-text)", icon: Coins,       label: "Optimalizace" },
};

const SEV_BG = {
  high:   "color-mix(in srgb, var(--cat-c) 12%, transparent)",
  medium: "color-mix(in srgb, var(--warn) 12%, transparent)",
  low:    "color-mix(in srgb, var(--info) 10%, transparent)",
};
const SEV_LABEL = { high: "VYSOKÁ", medium: "STŘEDNÍ", low: "NÍZKÁ" };

export default function AIWatchdogOpportunities({ contactId, compact = false, autoScan = true, onAfterActivate }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [activating, setActivating] = useState(null);
  const [scannedAt, setScannedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const url = contactId ? `/api/watchdog/opportunities/${contactId}` : "/api/watchdog/scan";
      const { data } = await api.get(url);
      setItems(asArr(data?.opportunities));
      if (data?.scanned_at) setScannedAt(data.scanned_at);
    } catch {
      // CHYBA != prázdno — nesmíme ukázat zelené „vše pod kontrolou", když sken selhal.
      setError(true);
      setItems([]);
      toast.error("Nelze načíst AI příležitosti");
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { if (autoScan) load(); }, [load, autoScan]);

  const handleActivate = async (op) => {
    if (op.existing_deal_id) {
      navigateOverlaySafe(navigate, `/obchody/${op.existing_deal_id}`);
      return;
    }
    setActivating(op.id);
    try {
      const { data } = await api.post("/api/watchdog/activate", { opportunity_id: op.id });
      toast.success(
        data.deal_created || data.task_created
          ? "Task + deal vytvořeny — najdi je v Diáři a Obchodech"
          : "Tato příležitost už je rozpracovaná"
      );
      await load();
      onAfterActivate?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nelze aktivovat");
    } finally {
      setActivating(null);
    }
  };

  const handleDismiss = async (op) => {
    try {
      await api.post("/api/watchdog/dismiss", { opportunity_id: op.id, snooze_days: 30 });
      toast(`Odloženo o 30 dní · ${op.contact_name || ""}`);
      setItems((prev) => prev.filter((i) => i.id !== op.id));
    } catch { toast.error("Nelze odložit"); }
  };

  const rescan = async () => {
    setScanning(true);
    try { await load(); toast.success("Sken dokončen"); }
    finally { setScanning(false); }
  };

  if (loading) {
    return <div className="py-8 text-center text-sm" style={{ color: "var(--muted)" }}>AI hlídač zkoumá smlouvy…</div>;
  }

  return (
    <div className="space-y-3" data-testid="watchdog-opportunities">
      {!compact && (
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-popisek uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--accent)" }}>
              <BellRing size={11} /> AI hlídač smluv
            </div>
            <div className="text-[12px] mt-0.5" style={{ color: "var(--muted)" }}>
              {error
                ? "Sken se nezdařil — zkus to prosím znovu"
                : items.length === 0
                ? "Žádné aktivní servisní příležitosti — pokrytí je v pořádku"
                : `${items.length} ${items.length === 1 ? "příležitost" : items.length <= 4 ? "příležitosti" : "příležitostí"} čeká na akci`}
              {scannedAt && (
                <span> · sken {new Date(scannedAt).toLocaleString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}</span>
              )}
            </div>
          </div>
          <button
            onClick={rescan}
            disabled={scanning}
            data-testid="watchdog-rescan"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-drobny font-semibold disabled:opacity-50"
            style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent)" }}
          >
            <RefreshCw size={11} className={scanning ? "animate-spin" : ""} /> {scanning ? "Skenuji…" : "Spustit nový sken"}
          </button>
        </div>
      )}

      {error ? (
        <div className="rounded-2xl p-6 text-center" data-testid="watchdog-error" style={{ background: "color-mix(in srgb, var(--cat-c) 8%, var(--surface))", border: "1px solid color-mix(in srgb, var(--cat-c) 30%, var(--line))" }}>
          <AlertTriangle size={20} className="mx-auto mb-2" style={{ color: "var(--cat-c)" }} />
          <div className="text-[13px] mb-1" style={{ color: "var(--ink)" }}>Příležitosti se nepodařilo načíst</div>
          <div className="text-drobny mb-3" style={{ color: "var(--muted)" }}>Nejde o prázdný seznam — sken smluv selhal. Zkus to prosím znovu.</div>
          <button
            onClick={load}
            data-testid="watchdog-retry"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-drobny font-semibold"
            style={{ background: "var(--accent)", color: "var(--on-accent)" }}
          >
            <RotateCcw size={11} /> Zkusit znovu
          </button>
        </div>
      ) : items.length === 0 ? (
        compact ? null : (
          <div className="rounded-2xl p-6 text-center" style={{ background: "color-mix(in srgb, var(--positive) 8%, var(--surface))", border: "1px solid color-mix(in srgb, var(--positive) 30%, var(--line))" }}>
            <Check size={20} className="mx-auto mb-2" style={{ color: "var(--positive)" }} />
            <div className="text-[13px] mb-1" style={{ color: "var(--ink)" }}>Vše je pod kontrolou</div>
            <div className="text-drobny" style={{ color: "var(--muted)" }}>AI hlídač nedetekoval žádné aktivní příležitosti — refixace, výročí ani investiční audity nejsou aktuálně potřeba.</div>
          </div>
        )
      ) : (
        items.map((op, i) => (
          <OpportunityCard
            key={op.id}
            op={op}
            i={i}
            activating={activating === op.id}
            onActivate={() => handleActivate(op)}
            onDismiss={() => handleDismiss(op)}
            onOpenClient={() => openClientFolder({ contactId: op.contact_id, section: "prilezitosti", source: "ai-watchdog" })}
            showClient={!contactId}
          />
        ))
      )}
    </div>
  );
}

function OpportunityCard({ op, i, activating, onActivate, onDismiss, onOpenClient, showClient }) {
  const meta = KIND_META[op.kind] || KIND_META.investment;
  const I = meta.icon;
  const sev = op.severity || "low";
  const isExisting = !!op.existing_deal_id;
  const bezPohybu = useBezPohybu();
  return (
    <motion.div
      initial={bezPohybu ? false : { opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={bezPohybu ? { duration: 0 } : { delay: Math.min(i, MAX_KROKU) * KROK, duration: UI, ease: EASE_OUT }}
      className="rounded-xl p-4 relative overflow-hidden"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        boxShadow: "var(--shadow-card)",
      }}
      data-testid={`opportunity-${op.id}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: meta.color }} />
      <div className="flex items-start gap-3 pl-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `color-mix(in srgb, ${meta.color} 14%, transparent)`, color: meta.color }}>
          <I size={15} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13px] font-semibold" style={{ color: "var(--ink)" }}>{op.title}</div>
            <span className="text-popisek uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                  style={{ color: meta.text || meta.color, background: SEV_BG[sev], border: `1px solid ${meta.color}` }}>
              {SEV_LABEL[sev] || "INFO"}
            </span>
            {isExisting && (
              <span className="text-popisek uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                    style={{ color: "var(--accent)", background: "var(--accent-soft)" }}>
                ROZPRACOVÁNO
              </span>
            )}
          </div>
          {showClient && (
            <button onClick={onOpenClient} className="text-drobny mt-0.5 inline-flex items-center gap-1 hover:underline" style={{ color: "var(--accent)" }} data-testid={`opportunity-client-${op.id}`}>
              {op.contact_name} <ChevronRight size={10} />
            </button>
          )}
          <div className="text-drobny mt-1 leading-relaxed" style={{ color: "var(--muted)" }}>{op.body}</div>
          <div className="mt-2 flex items-center gap-1.5 text-drobny flex-wrap">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "var(--bg)", color: "var(--ink)" }}>
              <ArrowRight size={10} style={{ color: meta.color }} /> {op.suggested_action}
            </span>
            {op.target_date && (
              <span className="inline-flex items-center gap-1" style={{ color: "var(--muted)" }}>
                <Clock size={10} /> termín {new Date(op.target_date).toLocaleDateString(localeUI())}
              </span>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onActivate}
              disabled={activating}
              data-testid={`opportunity-activate-${op.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-drobny font-semibold disabled:opacity-50 transition-opacity hover:opacity-90"
              style={{ background: isExisting ? "var(--accent-soft)" : "var(--accent)", color: isExisting ? "var(--accent)" : "var(--on-accent)", border: isExisting ? "1px solid var(--accent)" : "none" }}
            >
              {activating ? (
                <>Zakládám…</>
              ) : isExisting ? (
                <><Briefcase size={11} /> Otevřít rozpracovaný obchod</>
              ) : (
                <><CalendarPlus size={11} /> Vytvořit task + deal</>
              )}
            </button>
            {!isExisting && (
              <button
                onClick={onDismiss}
                data-testid={`opportunity-dismiss-${op.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-drobny"
                style={{ color: "var(--muted)" }}
                title="Odložit o 30 dní"
              >
                <XIcon size={11} /> Odložit
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
